import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { formValueSelector, change, clearSubmitErrors } from "redux-form";
import ClaimSubmissionForm from "./ClaimSubmissionForm";
import {
  getClaimItem,
  getBenefitPlan,
  getClaim,
  getFieldsByPlanIdAndItemType,
  augmentClaimFieldsProps,
} from "reducers/claimsReducer";
import {
  loadBenefitPlans,
  loadClaimItems,
  loadClaimItemFields,
  loadClaim,
  changeReferralFileName,
  changeCurrencyCode,
  changeCurrencyRate,
} from "actions/claimActions";
import { showToast } from "actions/toastActions";

import { Loader } from "cxa-components";

import { get } from "lodash";
import moment from "moment";

class EditClaimPage extends React.Component {
  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    this.loadDynamicData(prevProps);
  }

  loadData() {
    const {
      loadBenefitPlans,
      loadClaimItems,
      loadClaim,
      loadClaimItemFields,
      claimId,
      claimTypeId,
      benefitPeriod,
      mode,
      selectedDelegator,
      getDonateCategory,
    } = this.props;

    loadBenefitPlans();

    if (benefitPeriod) {
      loadClaimItems(benefitPeriod, false, selectedDelegator, getDonateCategory);
    }

    loadClaim(claimId, selectedDelegator);

    if (claimTypeId && benefitPeriod) {
      loadClaimItemFields(
        benefitPeriod,
        claimTypeId,
        claimId,
        mode,
        selectedDelegator,
        getDonateCategory
      );
    }
  }

  loadDynamicData(prevProps) {
    const {
      loadClaimItems,
      loadClaimItemFields,
      benefitPeriod,
      claimTypeId,
      claimId,
      mode,
      selectedDelegator,
      getDonateCategory,
    } = this.props;

    if (prevProps.benefitPeriod !== this.props.benefitPeriod) {
      loadClaimItems(benefitPeriod, false, selectedDelegator, getDonateCategory);
    }

    if (prevProps.claimTypeId !== this.props.claimTypeId && benefitPeriod && claimTypeId) {
      loadClaimItemFields(
        benefitPeriod,
        claimTypeId,
        claimId,
        mode,
        selectedDelegator,
        getDonateCategory
      );
    }
  }

  render() {
    const { isFetching } = this.props;

    return isFetching ? (
      <Loader />
    ) : (
      <div className="o-layout">
        <div className="o-layout__item u-1/2@desktop">
          <ClaimSubmissionForm {...this.props} />
        </div>
      </div>
    );
  }
}

EditClaimPage.propTypes = {
  loadBenefitPlans: PropTypes.func,
  loadClaimItems: PropTypes.func,
  loadClaim: PropTypes.func,
  loadClaimItemFields: PropTypes.func,
  changeReferralFileName: PropTypes.func,
  claimId: PropTypes.string,
  claimTypeId: PropTypes.string,
  submitClaim: PropTypes.func,
  benefitPeriod: PropTypes.string,
  isFetching: PropTypes.bool,
  mode: PropTypes.string,
  selectedDelegator: PropTypes.number,
  getDonateCategory: PropTypes.bool,
};

const selector = formValueSelector("editClaimForm");

const mapStateToProps = (
  {
    claims: { claimList, claimItems, fields, benefitPlans, toCurrencyCode },
    httpIntercept: { error },
    ...restState
  },
  {
    goBack,
    match: {
      params: { claimId, selectedDelegator: delegator },
    },
  }
) => {
  const today = moment().startOf("day");
  const claim = getClaim(claimList, claimId) || {};
  const selectedDelegator = delegator ? parseInt(delegator) : null;

  const {
    claimTypeId,
    benefitPeriod,
    referral,
    receipts,
    foreignReceiptAmount,
    receiptAmount,
    postalCode,
    providerName
  } = claim;
  const userAccepted = false;
  const benefitPlan = getBenefitPlan(benefitPlans, benefitPeriod);
  const { planStart, planEnd } = benefitPlan ? benefitPlan : { planStart: null, planEnd: null };
  const fieldsById = getFieldsByPlanIdAndItemType(fields, benefitPeriod, claimTypeId);

  const startDate = moment(planStart).startOf("day");
  const endDate = moment(planEnd).endOf("day");

  const minDate = startDate;
  const maxDate = today.isAfter(endDate) ? endDate : today;

  const _fields = fieldsById && {
    ...fieldsById,
    fields: augmentClaimFieldsProps(fieldsById.fields),
  };

  const initRenameFileValue = (_fields, referral) => {
    const file = get(_fields, "uploadReferral.uploadedFiles", [])
      .filter(file => file && referral && file.fkReferenceKey === referral.fkReferenceKey)
      .pop();

    if (file) {
      return file.value;
    }

    return "";
  };

  const initialValues = !_fields
    ? null
    : _fields.fields.reduce(
        (obj, field) => {
          return {
            ...obj,
            ...(field.renameFieldId
              ? {
                  [field.renameFieldId]: initRenameFileValue(_fields, referral),
                }
              : { [field.name]: claim.lowercasedKeys[field.fieldLowercased || field.name] }),
          };
        },
        { objectId: claimId, claimTypeId, benefitPeriod, referral, receipts }
      );

  const receiptCurrencyField = get(fieldsById, "fields", []).find(
    item => item.name === "receiptCurrency"
  );

  const defaultReceiptCurrency = (get(receiptCurrencyField, "items", [])[0] || {}).value || null;
  const receiptCurrencyCode =
    (_fields && selector(restState, "receiptCurrency")) || defaultReceiptCurrency;
  const approvedAmount =
    (_fields && selector(restState, "foreignReceiptAmount")) || foreignReceiptAmount;
  const receiptDate =
    _fields && (selector(restState, "receiptDate") || maxDate.format("YYYY-MM-DD[T]HH:mm:ss"));
  const postalCodeValue = 
    _fields && (selector(restState, "postalCode") || postalCode);
  const providerNameValue =
    _fields && (selector(restState, "providerName") || providerName);
  const claimItem = getClaimItem(claimItems, claimTypeId);

  const { isCPFEnabled = false } = claimItems;

  if (initialValues) {
    initialValues.foreignReceiptAmount = approvedAmount;
    initialValues.receiptAmount = receiptAmount;
    
    initialValues.selectedDelegator = selectedDelegator;
    initialValues.userAccepted = userAccepted;

    initialValues.postalCode = postalCodeValue
    initialValues.providerName = providerNameValue
  }

  return {
    mode: "edit",
    claim,
    claimId,
    claimTypeId,
    benefitPeriod,
    fields: _fields,
    benefitPlan,
    minDate,
    maxDate,
    claimItem,
    httpError: error,
    error,
    receiptDate,
    receiptCurrency: receiptCurrencyCode,
    foreignReceiptAmount: approvedAmount,
    userAccepted,
    receiptAmount,
    toCurrencyCode,
    initialValues,
    consentMessageTitle: fields.consentMessageTitle,
    consentMessage: fields.consentMessage,
    isFetching:
      !_fields ||
      fields.isFetching ||
      benefitPlans.isFetching ||
      claimItems.isFetching ||
      !claimItem ||
      (!benefitPlan && !claimItem),
    onCancel: goBack,
    isCPFEnabled,
    selectedDelegator,
  };
};

const mapDispatchToProps = {
  loadBenefitPlans,
  loadClaim,
  loadClaimItems,
  loadClaimItemFields,
  showToast,
  changeReferralFileName,
  changeCurrencyCode,
  changeCurrencyRate,
  changeFieldValue: (field, value) => dispatch => {
    dispatch(change("editClaimForm", field, value));
    dispatch(change("addEditClaim", field, value));
  },
  clearSubmitErrors,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditClaimPage);



// WEBPACK FOOTER //
// ./components/claim/EditClaimPage.js